import DownloadIcon from '@mui/icons-material/Download'
import PrintIcon from '@mui/icons-material/Print'
import {
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useCustomers, useServiceTypes } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import { BookingDiaryCard } from '@/pages/reports/BookingDiaryCard'
import { DocumentsPrintedCard } from '@/pages/reports/DocumentsPrintedCard'
import { REPORTS, reportByPath, reportPath } from '@/pages/reports/catalogue'
import { EmptyState, ErrorBanner, InlineSpinner, isoDate, PageHeader } from '@/components/common'

/** What a report comes back as, before it is a spreadsheet. */
type ReportTable = {
  title: string
  headers: string[]
  rows: (string | number | null)[][]
}

/**
 * One report, read on screen or taken away as a workbook.
 *
 * <p>Each has its own address, so the menu links straight to it and somebody
 * can bookmark the one they run every month rather than hunting for it in a
 * list. Without a report named in the address this is that list.</p>
 */
export function ReportsPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const can = useAuthStore((state) => state.can)
  // Opens on today. Every list and every report starts on the day being
  // worked, and the range is widened when somebody wants history.
  const [from, setFrom] = useState(() => isoDate(0))
  const [to, setTo] = useState(() => isoDate(0))
  const [status, setStatus] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [customerSearch, setCustomerSearch] = useState('')
  const [serviceType, setServiceType] = useState('')
  const [channel, setChannel] = useState('')
  // The branch chosen in the toolbar. A report read while looking at one
  // outlet should be that outlet's, without anybody choosing it twice.
  const activeOutletId = useAuthStore((state) => state.activeOutletId)
  const { data: serviceTypes } = useServiceTypes()

  const available = REPORTS.filter((report) => can(report.permission))
  const chosen = reportByPath(slug)
  const allowed = chosen && can(chosen.permission) ? chosen : undefined

  // Only where the report offers it; a customer list fetched for a staff
  // roster is a query nobody asked for.
  const { data: customers } = useCustomers(
    allowed?.byCustomer ? { q: customerSearch || undefined, size: 20 } : { size: 1 },
  )

  const filters = {
    from,
    to,
    status: status || undefined,
    customerId: customerId || undefined,
    // Sent only where the rows carry one, so a report that is company-wide by
    // nature is not quietly cut to a branch and shown as a whole.
    outletId: allowed?.byOutlet ? activeOutletId ?? undefined : undefined,
    serviceType: allowed?.byService ? serviceType || undefined : undefined,
    channel: allowed?.byChannel ? channel || undefined : undefined,
  }

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'report', allowed?.type, from, to, status, customerId,
      filters.outletId ?? 'all', serviceType, channel,
    ] as const,
    queryFn: () => api.get<ReportTable>(`/api/v1/reports/${allowed?.type}`, filters),
    enabled: Boolean(allowed),
  })

  // A filter left set while moving to a report that has no such column would
  // quietly narrow it to nothing.
  useEffect(() => {
    setStatus('')
    setCustomerId('')
    setServiceType('')
    setChannel('')
  }, [allowed?.type])

  const dateRange = (
    <Stack direction="row" spacing={1}>
      <TextField
        label="From"
        type="date"
        size="small"
        value={from}
        onChange={(event) => setFrom(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="To"
        type="date"
        size="small"
        value={to}
        onChange={(event) => setTo(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    </Stack>
  )

  // ------------------------------------------------------------- the list
  // A book to print, not a report to read: its own address, its own screen.
  if (slug === 'booking-diary') {
    return (
      <>
        <PageHeader
          title="Booking diary"
          subtitle="The book kept at the counter, printed blank to write in"
        />
        <BookingDiaryCard />
      </>
    )
  }

  // Counts that open into names and times, not a table to export: its own
  // address and its own screen, like the diary above.
  if (slug === 'documents-printed') {
    return (
      <>
        <PageHeader
          title="Documents printed"
          subtitle="What left the building, how often, and who took it"
        />
        <DocumentsPrintedCard />
      </>
    )
  }

  if (!allowed) {
    return (
      <>
        <PageHeader title="Reports" subtitle="Pick one, or reach it from the menu" />
        {available.length === 0 ? (
          <EmptyState message="No reports are available to you." />
        ) : (
          <Stack spacing={2}>
            {available.map((report) => (
              <Card key={report.type}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ sm: 'center' }}
                  >
                    <div>
                      <Typography variant="subtitle1">{report.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {report.about}
                      </Typography>
                    </div>
                    <Button size="small" onClick={() => navigate(reportPath(report.type))}>
                      Open
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </>
    )
  }

  // ------------------------------------------------------------ one report
  return (
    <>
      <PageHeader
        title={allowed.name}
        subtitle={allowed.about}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {/* A date range on a report that ignores it is a figure somebody
                will misread, so the ones that ignore it say so and do not
                offer it. */}
            {allowed.dated ? dateRange : (
              <Chip size="small" variant="outlined" label="As it stands today" />
            )}
            {/* Said out loud. A report narrowed to one branch and read as the
                whole business is a wrong total nobody questions. */}
            {allowed.byOutlet && activeOutletId ? (
              <Chip size="small" color="info" variant="outlined" label="This outlet only" />
            ) : null}
            {allowed.statuses ? (
              <TextField
                select
                size="small"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">Any status</MenuItem>
                {allowed.statuses.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {allowed.byChannel ? (
              <TextField
                select
                size="small"
                label="Channel"
                value={channel}
                onChange={(event) => setChannel(event.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">Every channel</MenuItem>
                <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                <MenuItem value="EMAIL">Email</MenuItem>
                <MenuItem value="SMS">SMS</MenuItem>
              </TextField>
            ) : null}
            {allowed.byService ? (
              <TextField
                select
                size="small"
                label="Service"
                value={serviceType}
                onChange={(event) => setServiceType(event.target.value)}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="">Every service</MenuItem>
                {(serviceTypes ?? []).filter((type) => type.active).map((type) => (
                  <MenuItem key={type.code} value={type.code}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {allowed.byCustomer ? (
              <TextField
                select
                size="small"
                label="Customer"
                value={customerId}
                onChange={(event) => setCustomerId(event.target.value)}
                onFocus={() => setCustomerSearch('')}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">Every customer</MenuItem>
                {(customers?.content ?? []).map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {/* The same table, landscape and fit to A4 - for a desk that wants
                it on paper rather than in a workbook. */}
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => void api.print(`/api/v1/reports/${allowed.type}/print`, filters)}
            >
              Print
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() =>
                api.download(`/api/v1/reports/${allowed.type}/export`, filters,
                  `${allowed.type.toLowerCase()}_${from}_to_${to}.xlsx`)
              }
            >
              Excel
            </Button>
          </Stack>
        }
      />

      <ErrorBanner error={error} />
      {isLoading ? <InlineSpinner /> : null}

      {data ? (
        <Card>
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              {data.title}
            </Typography>
            {data.rows.length === 0 ? (
              <EmptyState message="Nothing in this period." />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      {data.headers.map((header) => (
                        <TableCell key={header}>{header}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rows.map((row, index) => (
                      <TableRow key={index} hover>
                        {row.map((cell, cellIndex) => (
                          <TableCell
                            key={cellIndex}
                            align={typeof cell === 'number' ? 'right' : 'left'}
                          >
                            {/* Every report names the order the same way, so
                                this is the one place that has to know how to
                                reach one - any report carrying this column
                                opens it in the orders list rather than
                                leaving the number sitting there unreachable. */}
                            {data.headers[cellIndex] === 'Order No' && cell ? (
                              <Link
                                component={RouterLink}
                                to={`/orders?q=${encodeURIComponent(String(cell))}`}
                              >
                                {cell}
                              </Link>
                            ) : (
                              (cell ?? '—')
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </>
  )
}
